import React, { useState } from "react";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { Document, Packer, Paragraph, TextRun } from "docx";

const JobPostDetails = ({ jobDetails }) => {
  const [fileType, setFileType] = useState("text");

  const handleFileTypeChange = (event) => {
    setFileType(event.target.value);
  };

  const handleDownloadJD = () => {
    if (fileType === "text") {
      downloadAsText();
    } else if (fileType === "pdf") {
      downloadAsPDF();
    } else if (fileType === "word") {
      downloadAsWord();
    }
  };

  const downloadAsText = () => {
    const element = document.createElement("a");
    const file = new Blob([jobDetails.jobDescription], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `${jobDetails.jobTitle}_JobDescription.txt`;
    document.body.appendChild(element);
    element.click();
  };

  const downloadAsPDF = () => {
    const doc = new jsPDF();
    doc.text(jobDetails.jobDescription, 10, 10);
    doc.save(`${jobDetails.jobTitle}_JobDescription.pdf`);
  };

  const downloadAsWord = () => {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [new TextRun(jobDetails.jobDescription)],
            }),
          ],
        },
      ],
    });
    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `${jobDetails.jobTitle}_JobDescription.docx`);
    });
  };

  return (
    <div className="space-y-6">
      {/* Professional Header with Glass Effect */}
      <div 
        className="p-6 rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.05) 100%)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent mb-2">
          {jobDetails.jobTitle}
        </h2>
        <p className="text-blue-600 font-medium">{jobDetails.selectedCategory} • {jobDetails.workMode}</p>
      </div>

      {/* Job Details Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <div 
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              backdropFilter: 'blur(5px)'
            }}
          >
            <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
              </svg>
              Experience Level
            </h3>
            <p className="text-slate-700">{jobDetails.selectedExperience}</p>
          </div>

          <div 
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              backdropFilter: 'blur(5px)'
            }}
          >
            <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Skills Required
            </h3>
            <p className="text-slate-700">{jobDetails.skill}</p>
          </div>

          <div 
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              backdropFilter: 'blur(5px)'
            }}
          >
            <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              Compensation
            </h3>
            <p className="text-slate-700">{jobDetails.compensation}</p>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <div 
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              backdropFilter: 'blur(5px)'
            }}
          >
            <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
              Benefits
            </h3>
            <p className="text-slate-700">{jobDetails.benefits}</p>
          </div>

          <div 
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              backdropFilter: 'blur(5px)'
            }}
          >
            <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Created
            </h3>
            <p className="text-slate-700">{jobDetails.createdAt}</p>
          </div>

          <div 
            className="p-4 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              backdropFilter: 'blur(5px)'
            }}
          >
            <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Last Updated
            </h3>
            <p className="text-slate-700">{jobDetails.updatedAt}</p>
          </div>
        </div>
      </div>

      {/* Job Description Section */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
          border: '1px solid rgba(59, 130, 246, 0.15)',
          backdropFilter: 'blur(5px)'
        }}
      >
        <h3 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Job Description
        </h3>
        <div className="prose prose-blue max-w-none">
          <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{jobDetails.jobDescription}</p>
        </div>
      </div>

      {/* Enhanced Download Section */}
      <div 
        className="p-6 rounded-xl"
        style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.05) 100%)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download Job Description
        </h3>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-blue-800 mb-2" htmlFor="fileType">
              Select Format:
            </label>
            <select
              id="fileType"
              value={fileType}
              onChange={handleFileTypeChange}
              className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-300 transition-all duration-300 bg-white/80 text-slate-800"
              style={{
                color: '#1e293b'
              }}
            >
              <option value="text" style={{ color: '#1e293b', backgroundColor: '#ffffff' }}>Text File (.txt)</option>
              <option value="pdf" style={{ color: '#1e293b', backgroundColor: '#ffffff' }}>PDF Document (.pdf)</option>
              <option value="word" style={{ color: '#1e293b', backgroundColor: '#ffffff' }}>Word Document (.docx)</option>
            </select>
          </div>
          <button
            onClick={handleDownloadJD}
            className="inline-flex items-center px-6 py-3 text-sm font-semibold text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobPostDetails;
